import { useParams } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useFormik } from 'formik';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { Button } from '@/components/Button/Button';
import { OrganizationUserType } from '@/bundle/_Opco/types/types';
import { FormikPhoneInput } from '@/components/form/fields/FormikPhoneInput/FormikPhoneInput';
import { CheckPinCodeModal } from '@/bundle/shared/components/CheckPinCodeModal/CheckPinCodeModal';
import { PIN_TOKEN_EVENT_SCOPE } from '@/const/shared';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { Notification } from '@/components/Notification/Notification';
import { ButtonGroup } from '@/components/Button/ButtonGroup/ButtonGroup';
import { MOBILE_PHONE_VALIDATION_SCHEMA } from '@/const/formConfigs';
import { editOrganizationUserMobilePhone } from '@/bundle/_Opco/ui/OrganizationDetails/Details/api/api';

interface EditPhoneNumberFormType {
  mobilePhone: OrganizationUserType['mobile_phone'];
  onClose: () => void;
  onSuccess: () => void;
}

export const EditMobilePhoneForm = ({ onClose, mobilePhone, onSuccess }: EditPhoneNumberFormType) => {
  const { organizationId, userId } = useParams<{ organizationId: string; userId: string }>();
  const [isPinCodeOpen, setIsPinCodeOpen] = useState(false);

  const closePinCode = () => {
    setIsPinCodeOpen(false);
  };

  const formik = useFormik({
    initialValues: {
      mobile_phone: mobilePhone,
    },
    validationSchema: MOBILE_PHONE_VALIDATION_SCHEMA,
    enableReinitialize: false,
    onSubmit: () => {
      setIsPinCodeOpen(true);
    },
  });

  const { data, mutate, isPending } = useMutation({
    mutationKey: ['edit_mobile_phone'],
    mutationFn: (pinCode: string) => {
      return editOrganizationUserMobilePhone(organizationId, userId, formik.values?.mobile_phone, pinCode);
    },

    onSuccess(response) {
      if (response.error) return;

      onSuccess();
    },
  });

  const checkPinCodeSuccess = (pinToken: string) => {
    mutate(pinToken);
    closePinCode();
  };

  const submitFormOnEnter = () => {
    formik.handleSubmit();
  };

  const formError = getResponseError(data?.error);

  return (
    <>
      {formError && (
        <Notification variant='error' marginBottom='24px'>
          {formError}
        </Notification>
      )}
      <FormikForm value={formik}>
        <FormikPhoneInput
          name='mobile_phone'
          label='Mobile Phone Number*'
          placeholder='Enter Mobile Phone'
          apiError={data?.error}
          onEnterKeyPress={submitFormOnEnter}
        />
        <ButtonGroup marginTop='12px'>
          <Button type='button' variant='secondary' onClick={onClose}>
            Cancel
          </Button>
          <Button type='submit' isLoading={isPending}>
            Save
          </Button>
        </ButtonGroup>
      </FormikForm>
      {isPinCodeOpen && (
        <CheckPinCodeModal
          isOpen={isPinCodeOpen}
          onSuccess={checkPinCodeSuccess}
          onClose={closePinCode}
          eventScope={PIN_TOKEN_EVENT_SCOPE.EDIT_ORG_ADMIN}
        />
      )}
    </>
  );
};
